<template>
    <div class="relative min-h-screen w-full flex bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50">
        <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-6 max-w-6xl">
            <!-- Breadcrumb -->
            <nav class="text-sm text-gray-600 mb-6 flex flex-wrap items-center">
                <router-link to="/dashboard"
                    class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
                <span class="mx-1">/</span>
                <router-link to="/survey-and-feedback-center"
                    class="hover:text-blue-700 transition font-medium">Survey & Feedback Center</router-link>
                <span class="mx-1">/</span>
                <router-link to="/survey"
                    class="hover:text-blue-700 transition font-medium">Surveys</router-link>
                <span class="mx-1">/</span>
                <span class="text-blue-900 font-semibold">Responses</span>
            </nav>

            <!-- Header Card -->
            <div class="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 mb-6">
                <div class="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 px-6 sm:px-8 py-6">
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div class="flex items-center space-x-4 flex-1">
                            <div class="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                                <font-awesome-icon icon="comments" class="text-2xl text-white" />
                            </div>
                            <div class="flex-1">
                                <h1 class="text-2xl sm:text-3xl font-bold text-white">
                                    {{ survey ? survey.title : 'Survey Responses' }}
                                </h1>
                                <p class="text-blue-100 text-sm sm:text-base">View and analyze survey responses</p>
                            </div>
                        </div>
                        <div class="flex items-center space-x-3 sm:space-x-4">
                            <button v-if="responses && responses.length > 0" @click="exportToExcel"
                                :disabled="exportLoading"
                                class="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg font-semibold transition-all shadow-sm hover:shadow-md flex items-center disabled:opacity-50">
                                <font-awesome-icon v-if="exportLoading" icon="spinner" spin class="mr-2" />
                                <font-awesome-icon v-else icon="file-excel" class="mr-2" />
                                <span class="hidden sm:inline">{{ exportLoading ? 'Exporting...' : 'Export Excel' }}</span>
                                <span class="sm:hidden">{{ exportLoading ? '...' : 'Excel' }}</span>
                            </button>
                            <button v-if="responses && responses.length > 0" @click="openAnalysisModal"
                                class="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg font-semibold transition-all shadow-sm hover:shadow-md flex items-center">
                                <font-awesome-icon icon="chart-pie" class="mr-2" />
                                <span class="hidden sm:inline">Analyze Responses</span>
                                <span class="sm:hidden">Analyze</span>
                            </button>
                            <button @click="goBack"
                                class="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg font-medium transition-all shadow-sm hover:shadow-md flex items-center">
                                <font-awesome-icon icon="arrow-left" class="mr-2" />
                                <span class="hidden sm:inline">Back</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Survey Link (compact) -->
            <div v-if="survey" class="sr-survey-link-bar mb-4">
                <span class="sr-survey-link-label">Survey Link</span>
                <div class="sr-survey-link-field" :title="surveyTakeLink">
                    <font-awesome-icon icon="link" class="sr-survey-link-icon" />
                    <span class="sr-survey-link-text">{{ surveyTakeLink }}</span>
                </div>
                <button type="button" class="sr-survey-link-copy" :title="surveyLinkCopied ? 'Copied!' : 'Copy survey link'"
                    @click="copySurveyLink">
                    <font-awesome-icon :icon="surveyLinkCopied ? 'check' : 'copy'" class="sr-survey-link-copy-icon" />
                    <span>{{ surveyLinkCopied ? 'Copied' : 'Copy' }}</span>
                </button>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="flex flex-col justify-center items-center min-h-96">
                <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
                <p class="mt-4 text-gray-600 text-lg">Loading responses...</p>
            </div>

            <!-- Responses List -->
            <div v-else class="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                <!-- Section Header -->
                <div class="px-6 sm:px-8 py-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div class="flex items-center justify-between sm:justify-start">
                            <div class="flex items-center">
                                <div class="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center mr-3">
                                    <font-awesome-icon icon="clipboard-check" class="text-white" />
                                </div>
                                <h2 class="text-xl font-bold text-gray-900">Responses</h2>
                            </div>
                            <span class="px-4 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold sm:ml-4">
                                {{ paginatorInfo ? paginatorInfo.total : responses.length }} {{ (paginatorInfo ? paginatorInfo.total : responses.length) === 1 ? 'Response' : 'Responses' }}
                            </span>
                        </div>
                        <div class="flex items-center space-x-2">
                            <span class="text-sm text-gray-600 font-medium">View:</span>
                            <div class="flex border border-gray-200 rounded-lg overflow-hidden">
                                <button @click="responseViewMode = 'list'"
                                    :class="responseViewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'"
                                    class="px-4 py-2 text-sm font-medium transition-colors flex items-center">
                                    <font-awesome-icon icon="list" class="mr-2" />
                                    List
                                </button>
                                <button @click="responseViewMode = 'table'"
                                    :class="responseViewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'"
                                    class="px-4 py-2 text-sm font-medium transition-colors flex items-center">
                                    <font-awesome-icon icon="table" class="mr-2" />
                                    Table
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Response status tabs -->
                <div class="border-b border-gray-200 bg-white">
                    <div class="flex overflow-x-auto">
                        <button type="button" @click="setResponseStatusTab('all')" :class="[
                            'flex-1 min-w-[120px] px-4 sm:px-6 py-3 text-sm font-medium transition-colors duration-200 whitespace-nowrap',
                            responseStatusTab === 'all'
                                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        ]">
                            All
                        </button>
                        <button type="button" @click="setResponseStatusTab('completed')" :class="[
                            'flex-1 min-w-[140px] px-4 sm:px-6 py-3 text-sm font-medium transition-colors duration-200 whitespace-nowrap',
                            responseStatusTab === 'completed'
                                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        ]">
                            Fully Completed
                        </button>
                        <button type="button" @click="setResponseStatusTab('partial')" :class="[
                            'flex-1 min-w-[150px] px-4 sm:px-6 py-3 text-sm font-medium transition-colors duration-200 whitespace-nowrap',
                            responseStatusTab === 'partial'
                                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        ]">
                            Partially Answered
                        </button>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-if="responses.length === 0" class="p-12 text-center">
                    <div class="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-4">
                        <font-awesome-icon icon="inbox" class="text-4xl text-gray-400" />
                    </div>
                    <h3 class="text-xl font-semibold text-gray-900 mb-2">{{ emptyResponsesTitle }}</h3>
                    <p class="text-gray-600">{{ emptyResponsesMessage }}</p>
                </div>

                <!-- Table View -->
                <div v-else-if="responseViewMode === 'table'" class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th scope="col"
                                    class="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                                    #
                                </th>
                                <th scope="col"
                                    class="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider sr-respondent-col">
                                    Respondent
                                </th>
                                <th scope="col"
                                    class="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[140px]">
                                    Progress
                                </th>
                                <th scope="col"
                                    class="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[110px]">
                                    Duration
                                </th>
                                <th scope="col"
                                    class="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[120px]">
                                    Status
                                </th>
                                <th scope="col"
                                    class="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[120px]">
                                    Submission
                                </th>
                                <th scope="col"
                                    class="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[120px]">
                                    Submitted at
                                </th>
                                <th scope="col"
                                    class="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[90px]">
                                    Answered
                                </th>
                                <th scope="col"
                                    class="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[80px]">
                                    Total
                                </th>
                                <th scope="col"
                                    class="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[130px]">
                                    Started At
                                </th>
                                <th scope="col"
                                    class="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[130px]">
                                    Last Activity At
                                </th>
                                <th v-for="q in sortedQuestions" :key="q.id" scope="col"
                                    class="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[160px] max-w-[240px]">
                                    <span class="line-clamp-2" :title="q.question_text">{{ q.question_text }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            <tr v-for="(response, rIdx) in responses" :key="response.id"
                                class="hover:bg-gray-50 transition-colors">
                                <td class="px-4 py-3 text-sm text-gray-600 whitespace-nowrap sticky left-0 bg-white z-0">
                                    {{ (paginatorInfo && paginatorInfo.perPage) ? (paginatorInfo.currentPage - 1) * paginatorInfo.perPage + rIdx + 1 : rIdx + 1 }}
                                </td>
                                <td class="px-4 py-3 text-sm sr-respondent-col">
                                    <div class="sr-respondent-cell sr-respondent-cell-table">
                                        <span class="sr-respondent-name" :title="getRespondentName(response)">
                                            {{ getRespondentName(response) }}
                                        </span>
                                        <span v-if="response.token" class="sr-respondent-token-tile" :title="'Token: ' + response.token">
                                            <span class="sr-respondent-token-label">Token:</span>
                                            <span class="sr-respondent-token-value">{{ response.token }}</span>
                                        </span>
                                    </div>
                                </td>
                                <td class="px-4 py-3 text-sm text-gray-800 min-w-[140px]">
                                    <div class="survey-response-progress-wrap">
                                        <div class="survey-response-progress-track">
                                            <div class="survey-response-progress-fill"
                                                :class="getProgressBarClass(response)"
                                                :style="{ width: getCompletionPercentage(response) + '%' }"></div>
                                        </div>
                                        <span class="survey-response-progress-label">
                                            {{ Math.round(getCompletionPercentage(response)) }}%
                                        </span>
                                    </div>
                                </td>
                                <td class="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                    {{ formatEngagementDuration(response) }}
                                </td>
                                <td class="px-4 py-3 text-sm whitespace-nowrap">
                                    <span class="px-2 py-1 rounded text-xs font-semibold"
                                        :class="response.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'">
                                        {{ getResponseStatusLabel(response) }}
                                    </span>
                                </td>
                                <td class="px-4 py-3 text-sm whitespace-nowrap">
                                    <span class="px-2 py-1 rounded text-xs font-semibold"
                                        :class="response.is_submitted ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'">
                                        {{ getSubmissionLabel(response) }}
                                    </span>
                                </td>
                                <td class="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                    {{ response.is_submitted ? formatDate(response.completed_at || response.created_at) : '—' }}
                                </td>
                                <td class="px-4 py-3 text-sm text-gray-800 whitespace-nowrap text-center">
                                    {{ formatCountValue(response.answered_questions_count) }}
                                </td>
                                <td class="px-4 py-3 text-sm text-gray-800 whitespace-nowrap text-center">
                                    {{ formatCountValue(response.total_questions_count) }}
                                </td>
                                <td class="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                    {{ response.started_at ? formatDate(response.started_at) : '—' }}
                                </td>
                                <td class="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                    {{ response.last_activity_at ? formatDate(response.last_activity_at) : '—' }}
                                </td>
                                <td v-for="q in sortedQuestions" :key="q.id"
                                    class="px-4 py-3 text-sm text-gray-800 max-w-[240px]">
                                    <span class="line-clamp-2 block" :title="getTableCellAnswer(response, q)">
                                        {{ getTableCellAnswer(response, q) || '—' }}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- List View - Responses -->
                <div v-else class="divide-y divide-gray-200">
                    <div v-for="(response, responseIndex) in responses" :key="response.id"
                        class="p-6 sm:p-8 hover:bg-gray-50 transition-colors">
                        <!-- Response Header -->
                        <div class="flex items-start justify-between mb-4 pb-4 border-b border-gray-200">
                            <div class="flex items-start space-x-4 flex-1">
                                <div class="flex-shrink-0">
                                    <div
                                        class="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-md">
                                        <span class="text-white font-bold text-lg">#{{ responseIndex + 1 }}</span>
                                    </div>
                                </div>
                                <div class="flex-1 min-w-0">
                                    <div class="flex flex-wrap items-center gap-3 mb-2">
                                        <div class="flex items-center text-sm text-gray-600 min-w-0">
                                            <font-awesome-icon icon="user" class="mr-2 text-blue-600 flex-shrink-0" />
                                            <div class="sr-respondent-cell sr-respondent-cell-list min-w-0">
                                                <span class="sr-respondent-name" :title="getRespondentName(response)">
                                                    {{ getRespondentName(response) }}
                                                </span>
                                                <span v-if="response.token" class="sr-respondent-token-tile" :title="'Token: ' + response.token">
                                                    <span class="sr-respondent-token-label">Token:</span>
                                                    <span class="sr-respondent-token-value">{{ response.token }}</span>
                                                </span>
                                            </div>
                                        </div>
                                        <span class="px-2 py-1 rounded text-xs font-semibold"
                                            :class="response.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'">
                                            {{ getResponseStatusLabel(response) }}
                                        </span>
                                        <span class="px-2 py-1 rounded text-xs font-semibold"
                                            :class="response.is_submitted ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-700'">
                                            {{ getSubmissionLabel(response) }}
                                        </span>
                                        <span class="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                                            {{ response.answered_questions_count || 0 }}/{{ response.total_questions_count || 0 }} answered
                                        </span>
                                    </div>
                                    <div class="mb-3">
                                        <div class="survey-response-progress-wrap">
                                            <div class="survey-response-progress-track">
                                                <div class="survey-response-progress-fill"
                                                    :class="getProgressBarClass(response)"
                                                    :style="{ width: getCompletionPercentage(response) + '%' }"></div>
                                            </div>
                                            <span class="survey-response-progress-label">
                                                {{ Math.round(getCompletionPercentage(response)) }}% complete
                                            </span>
                                        </div>
                                    </div>
                                    <div class="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                                        <div class="flex items-center">
                                            <font-awesome-icon icon="clock" class="mr-2 text-indigo-600" />
                                            <span>{{ formatEngagementDuration(response) }}</span>
                                        </div>
                                        <div v-if="response.is_submitted" class="flex items-center">
                                            <font-awesome-icon icon="calendar-alt" class="mr-2 text-blue-600" />
                                            <span>Submitted: {{ formatDate(response.completed_at || response.created_at) }}</span>
                                        </div>
                                        <div v-else-if="response.last_activity_at" class="flex items-center">
                                            <font-awesome-icon icon="history" class="mr-2 text-amber-600" />
                                            <span>Last activity: {{ formatDate(response.last_activity_at) }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <button @click="toggleResponse(response.id)"
                                class="ml-4 flex-shrink-0 w-10 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all shadow-sm hover:shadow-md flex items-center justify-center">
                                <font-awesome-icon
                                    :icon="expandedResponses[response.id] ? 'chevron-up' : 'chevron-down'" />
                            </button>
                        </div>

                        <!-- Answers (Collapsible) -->
                        <transition name="slide-fade">
                            <div v-show="expandedResponses[response.id]" class="space-y-5">
                                <template v-for="item in getResponseDisplayItems(response)" :key="item.type === 'section' ? 'section-' + item.question.id : item.type === 'page_break' ? 'page-break-' + item.question.id : 'answer-' + item.question.id">
                                    <!-- Section Header -->
                                    <div v-if="item.type === 'section'"
                                        class="pt-4 mt-4 first:pt-0 first:mt-0"
                                        :style="getSectionHeaderBorderStyle(item.question)">
                                        <div class="flex items-center gap-3">
                                            <div class="flex-shrink-0 w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                                                <font-awesome-icon icon="heading" class="text-indigo-600 text-lg" />
                                            </div>
                                            <h3 class="text-lg font-bold"
                                                :style="{ color: getSectionTitleColor(item.question) }">
                                                {{ item.question.question_text || 'Section' }}
                                            </h3>
                                        </div>
                                    </div>

                                    <!-- Page Break -->
                                    <div v-else-if="item.type === 'page_break'"
                                        class="pt-4 mt-4 border-t-2 border-dashed border-violet-200">
                                        <div class="flex items-center gap-3">
                                            <div class="flex-shrink-0 w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                                                <font-awesome-icon icon="columns" class="text-violet-600 text-lg" />
                                            </div>
                                            <div>
                                                <h3 class="text-sm font-bold text-violet-800 uppercase tracking-wide">Page Break</h3>
                                                <p class="text-sm mt-0.5"
                                                    :class="getPageBreakTitleText(item.question) ? '' : 'text-violet-600'"
                                                    :style="getPageBreakTitleText(item.question) ? { color: getPageTitleColor(item.question) } : {}">
                                                    {{ getPageBreakTitleText(item.question) || 'New page starts here' }}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Question Answer -->
                                    <div v-else
                                        class="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-5 border-l-4 border-blue-500 hover:shadow-md transition-shadow">
                                        <div class="flex items-start space-x-3">
                                            <div class="flex-shrink-0 mt-1">
                                                <div
                                                    class="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                                                    <span class="text-white font-semibold text-xs">{{ item.displayIndex }}</span>
                                                </div>
                                            </div>
                                            <div class="flex-1 min-w-0">
                                                <p class="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                                                    <font-awesome-icon icon="question-circle" class="mr-2 text-blue-600" />
                                                    {{ item.question.question_text }}
                                                </p>
                                                <div class="bg-white rounded-lg p-4 border border-gray-200">
                                                <!-- Emoji Rating Answer -->
                                                <div v-if="item.answer && item.question.type === 'emoji_rating' && item.answer.answer_text"
                                                    class="flex items-center space-x-4">
                                                    <img :src="getEmojiRatingSvg(item.answer.answer_text)"
                                                        :alt="getEmojiRatingLabel(item.answer.answer_text)"
                                                        class="h-12 w-12 object-contain" />
                                                    <div class="flex flex-col">
                                                        <span class="text-gray-800 font-semibold text-base">
                                                            {{ getEmojiRatingLabel(item.answer.answer_text) }}
                                                        </span>
                                                        <span class="text-gray-500 text-sm">
                                                            Rating: {{ item.answer.answer_text }}/5
                                                        </span>
                                                    </div>
                                                </div>

                                                <!-- Star Rating Answer -->
                                                <div v-else-if="item.answer && item.question.type === 'rating' && item.answer.answer_text"
                                                    class="flex items-center space-x-4">
                                                    <div class="flex items-center space-x-1">
                                                        <span v-for="star in parseInt(item.answer.answer_text, 10)"
                                                            :key="star" class="text-3xl sm:text-4xl text-yellow-400">
                                                            ⭐
                                                        </span>
                                                    </div>
                                                    <div class="flex flex-col">
                                                        <span class="text-gray-800 font-semibold text-base">
                                                            {{ item.answer.answer_text }}/5 Stars
                                                        </span>
                                                        <span class="text-gray-500 text-sm">
                                                            {{ getRatingLabel(parseInt(item.answer.answer_text)) }}
                                                        </span>
                                                    </div>
                                                </div>

                                                <!-- Table Answer -->
                                                <div v-else-if="item.answer && item.question.type === 'table' && item.answer.answer_text"
                                                    class="overflow-x-auto -mx-1">
                                                    <table class="min-w-full border border-gray-300 rounded-lg">
                                                        <thead>
                                                            <tr class="bg-gray-50">
                                                                <th v-for="(col, colIndex) in sortedOptions(item.question.options)"
                                                                    :key="colIndex"
                                                                    class="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-300 border-r border-gray-300 last:border-r-0">
                                                                    {{ col.option_text }}
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr v-for="(row, rowIndex) in parseTableAnswer(item.answer.answer_text)"
                                                                :key="rowIndex"
                                                                class="border-b border-gray-300 hover:bg-gray-50">
                                                                <td v-for="(cell, colIndex) in row" :key="colIndex"
                                                                    class="px-4 py-3 text-gray-800 border-r border-gray-300 last:border-r-0">
                                                                    {{ cell }}
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>

                                                <!-- Grid Single Select Answer -->
                                                <div v-else-if="item.answer && item.question.type === 'grid_single_select' && item.answer.answer_text"
                                                    class="overflow-x-auto -mx-1">
                                                    <table class="min-w-full border border-gray-300 rounded-lg">
                                                        <thead>
                                                            <tr class="bg-gray-50">
                                                                <!-- Blank cell for row 0, column 0 -->
                                                                <th
                                                                    class="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-300 border-r border-gray-300">
                                                                </th>
                                                                <!-- Column headers (row 0, columns 1+) -->
                                                                <th v-for="(col, colIndex) in sortedOptions(item.question.options)"
                                                                    :key="colIndex"
                                                                    class="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-300 border-r border-gray-300 last:border-r-0">
                                                                    {{ col.option_text }}
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <!-- Data rows (rows 1+) -->
                                                            <tr v-for="(rowTitle, rowIndex) in (item.question.grid_row_titles || [])"
                                                                :key="rowIndex"
                                                                class="border-b border-gray-200 hover:bg-gray-50">
                                                                <!-- Row title (column 0) -->
                                                                <td
                                                                    class="px-4 py-3 text-sm font-semibold text-gray-700 border-r border-gray-200">
                                                                    {{ rowTitle }}
                                                                </td>
                                                                <!-- Display selected column for each row -->
                                                                <td v-for="(col, colIndex) in sortedOptions(item.question.options)"
                                                                    :key="colIndex"
                                                                    class="px-4 py-3 text-gray-800 border-r border-gray-200 last:border-r-0 text-center">
                                                                    <span
                                                                        v-if="parseGridAnswer(item.answer.answer_text)[rowIndex] == col.id"
                                                                        class="inline-flex items-center justify-center w-6 h-6 bg-blue-500 text-white rounded-full text-xs font-semibold">
                                                                        ✓
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>

                                                <!-- Grid Multiple Select Answer -->
                                                <div v-else-if="item.answer && item.question.type === 'grid_multiple_select' && item.answer.answer_text"
                                                    class="overflow-x-auto -mx-1">
                                                    <table class="min-w-full border border-gray-300 rounded-lg">
                                                        <thead>
                                                            <tr class="bg-gray-50">
                                                                <!-- Blank cell for row 0, column 0 -->
                                                                <th
                                                                    class="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-300 border-r border-gray-300">
                                                                </th>
                                                                <!-- Column headers (row 0, columns 1+) -->
                                                                <th v-for="(col, colIndex) in sortedOptions(item.question.options)"
                                                                    :key="colIndex"
                                                                    class="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-300 border-r border-gray-300 last:border-r-0">
                                                                    {{ col.option_text }}
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <!-- Data rows (rows 1+) -->
                                                            <tr v-for="(rowTitle, rowIndex) in (item.question.grid_row_titles || [])"
                                                                :key="rowIndex"
                                                                class="border-b border-gray-200 hover:bg-gray-50">
                                                                <!-- Row title (column 0) -->
                                                                <td
                                                                    class="px-4 py-3 text-sm font-semibold text-gray-700 border-r border-gray-200">
                                                                    {{ rowTitle }}
                                                                </td>
                                                                <!-- Display selected columns for each row (multiple selections) -->
                                                                <td v-for="(col, colIndex) in sortedOptions(item.question.options)"
                                                                    :key="colIndex"
                                                                    class="px-4 py-3 text-gray-800 border-r border-gray-200 last:border-r-0 text-center">
                                                                    <span
                                                                        v-if="isGridMultipleCellSelected(item.answer.answer_text, rowIndex, col.id)"
                                                                        class="inline-flex items-center justify-center w-6 h-6 bg-blue-500 text-white rounded text-xs font-semibold">
                                                                        ✓
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>

                                                <!-- Signature Answer -->
                                                <div v-else-if="item.answer && item.question.type === 'signature' && item.answer.answer_text"
                                                    class="flex items-center gap-2 flex-wrap">
                                                    <img :src="item.answer.answer_text" alt="Signature"
                                                        class="max-w-xs border border-gray-300 rounded-lg shadow-sm" />
                                                    <button
                                                        @click="downloadSignature(item.answer.answer_text, item.question.question_text)"
                                                        class="flex items-center justify-center bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm font-medium py-2 px-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer">
                                                        <font-awesome-icon icon="download" class="mr-2" />
                                                        Download
                                                    </button>
                                                </div>

                                                <!-- File Upload Answer -->
                                                <div v-else-if="item.answer && item.question.type === 'file_upload' && item.answer.answer_text"
                                                    class="flex items-center gap-2 flex-wrap">
                                                    <span class="text-sm text-gray-700 font-medium mr-2">{{
                                                        getSurveyFileName(item.answer.answer_text) }}</span>
                                                    <div class="flex space-x-2">
                                                        <button @click="viewSurveyFile(item.answer.answer_text)"
                                                            class="flex items-center justify-center bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-sm font-medium py-2 px-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer">
                                                            <font-awesome-icon icon="eye" class="mr-2" />
                                                            View
                                                        </button>
                                                        <button
                                                            @click="downloadSurveyFile(item.answer.answer_text, getSurveyFileName(item.answer.answer_text))"
                                                            class="flex items-center justify-center bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm font-medium py-2 px-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer">
                                                            <font-awesome-icon icon="download" class="mr-2" />
                                                            Download
                                                        </button>
                                                    </div>
                                                </div>

                                                <!-- Switch Answer (show label instead of 0/1) -->
                                                <p v-else-if="item.answer && item.question.type === 'switch'"
                                                    class="text-gray-800 font-medium">
                                                    {{ getSwitchAnswerLabel(item.question, item.answer.answer_text) }}
                                                </p>

                                                <!-- Text/Textarea Answer -->
                                                <p v-else-if="item.answer && item.answer.answer_text && item.question.type !== 'emoji_rating' && item.question.type !== 'rating' && item.question.type !== 'table' && item.question.type !== 'grid_single_select' && item.question.type !== 'grid_multiple_select' && item.question.type !== 'file_upload' && item.question.type !== 'signature' && item.question.type !== 'switch' && item.question.type !== 'radio' && item.question.type !== 'checkbox' && item.question.type !== 'select'"
                                                    class="text-gray-800 font-medium whitespace-pre-wrap">
                                                    {{ item.answer.answer_text }}
                                                </p>

                                                <!-- Single Option Answer -->
                                                <div v-else-if="item.answer && item.answer.option && ['radio', 'select'].includes(item.question.type)" class="space-y-2">
                                                    <div class="flex items-center space-x-2">
                                                        <span class="w-2 h-2 bg-blue-500 rounded-full"></span>
                                                        <span class="text-gray-800 font-medium">{{ item.answer.option.option_text
                                                            }}</span>
                                                    </div>
                                                    <p v-if="getOtherCommentText(item)"
                                                        class="text-gray-700 text-sm ml-4 pl-2 border-l-2 border-gray-200 whitespace-pre-wrap">
                                                        {{ getOtherCommentText(item) }}
                                                    </p>
                                                </div>

                                                <!-- Single Option Answer (other types) -->
                                                <div v-else-if="item.answer && item.answer.option" class="flex items-center space-x-2">
                                                    <span class="w-2 h-2 bg-blue-500 rounded-full"></span>
                                                    <span class="text-gray-800 font-medium">{{ item.answer.option.option_text
                                                        }}</span>
                                                </div>

                                                <!-- Multiple Options Answer -->
                                                <div v-else-if="item.answer && item.answer.options && item.answer.options.length > 0"
                                                    class="space-y-2">
                                                    <div v-for="option in item.answer.options" :key="option.id"
                                                        class="flex items-center space-x-2">
                                                        <span class="w-2 h-2 bg-blue-500 rounded-full"></span>
                                                        <span class="text-gray-800 font-medium">{{ option.option_text
                                                            }}</span>
                                                    </div>
                                                    <p v-if="getOtherCommentText(item)"
                                                        class="text-gray-700 text-sm ml-4 pl-2 border-l-2 border-gray-200 whitespace-pre-wrap">
                                                        {{ getOtherCommentText(item) }}
                                                    </p>
                                                </div>

                                                <!-- No answer -->
                                                <p v-else class="text-gray-400 italic">No answer provided</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                </template>

                                <!-- Contact Information Section -->
                                <div v-if="response.contact_information"
                                    class="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-5 border-l-4 border-green-500 hover:shadow-md transition-shadow">
                                    <div class="flex items-start space-x-3">
                                        <div class="flex-shrink-0 mt-1">
                                            <div
                                                class="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                                                <font-awesome-icon icon="address-card" class="text-white text-sm" />
                                            </div>
                                        </div>
                                        <div class="flex-1 min-w-0">
                                            <p class="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                                                <font-awesome-icon icon="user-circle" class="mr-2 text-green-600" />
                                                Contact Information
                                            </p>
                                            <div class="bg-white rounded-lg p-4 border border-gray-200 space-y-3">
                                                <div class="flex items-start space-x-3">
                                                    <font-awesome-icon icon="user"
                                                        class="text-green-600 mt-1 flex-shrink-0" />
                                                    <div class="flex-1">
                                                        <span class="text-xs text-gray-500 block mb-1">Name</span>
                                                        <span class="text-gray-800 font-medium">{{
                                                            response.contact_information.name }}</span>
                                                    </div>
                                                </div>
                                                <div class="flex items-start space-x-3">
                                                    <font-awesome-icon icon="envelope"
                                                        class="text-green-600 mt-1 flex-shrink-0" />
                                                    <div class="flex-1">
                                                        <span class="text-xs text-gray-500 block mb-1">Email</span>
                                                        <a :href="`mailto:${response.contact_information.email}`"
                                                            class="text-blue-600 hover:text-blue-800 font-medium break-all">
                                                            {{ response.contact_information.email }}
                                                        </a>
                                                    </div>
                                                </div>
                                                <div class="flex items-start space-x-3">
                                                    <font-awesome-icon icon="phone"
                                                        class="text-green-600 mt-1 flex-shrink-0" />
                                                    <div class="flex-1">
                                                        <span class="text-xs text-gray-500 block mb-1">Telephone</span>
                                                        <a :href="`tel:${response.contact_information.country_code}${response.contact_information.telephone}`"
                                                            class="text-blue-600 hover:text-blue-800 font-medium">
                                                            {{ response.contact_information.country_code }} {{
                                                                response.contact_information.telephone }}
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </transition>
                    </div>
                </div>

                <!-- Pagination (shared for both List and Table views) -->
                <div v-if="paginatorInfoWithItems && paginatorInfoWithItems.lastPage > 1"
                    class="px-6 sm:px-8 py-5 border-t border-gray-200 bg-gray-50">
                    <pagination :paginatorInfo="paginatorInfoWithItems" :setPage="setPage"></pagination>
                </div>
            </div>
        </div>
        <base-full-modal v-if="showAnalysisModal" :title="survey ? (survey.title + ' - Analysis') : 'Survey Analysis'"
            @close="closeAnalysisModal">
            <div class="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div class="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <h2 class="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
                            <font-awesome-icon icon="chart-pie" class="text-blue-600 mr-2" />
                            Survey Analysis
                        </h2>
                        <p class="text-sm text-gray-600 mt-1">
                            Visual breakdown of how respondents answered each question.
                        </p>
                    </div>
                    <div class="flex items-center space-x-2 text-sm text-gray-500">
                        <span
                            class="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 font-medium">
                            <font-awesome-icon icon="clipboard-check" class="mr-1" />
                            {{ responses.length }} {{ responses.length === 1 ? 'Response' : 'Responses' }}
                        </span>
                    </div>
                </div>

                <div v-if="analysisLoading" class="flex flex-col items-center justify-center py-16">
                    <base-page-spinner type="spin" class-list="h-16"></base-page-spinner>
                    <p class="mt-4 text-gray-600 text-base">Loading analysis...</p>
                </div>

                <div v-else>
                    <div v-if="!analysisQuestions || analysisQuestions.length === 0"
                        class="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center">
                        <div class="text-4xl mb-2">📊</div>
                        <h3 class="text-lg font-semibold text-gray-900 mb-1">No analysis data available</h3>
                        <p class="text-gray-600 text-sm">
                            Once responses are recorded for this survey, you will see question-wise charts here.
                        </p>
                    </div>
                    <div v-else class="grid gap-6 md:grid-cols-2">
                        <div v-for="(question, index) in analysisQuestions" :key="question.question_id"
                            class="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col">
                            <div class="mb-3">
                                <div class="flex items-start justify-between">
                                    <div class="flex-1">
                                        <p class="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">
                                            Question {{ index + 1 }}
                                        </p>
                                        <p class="text-sm sm:text-base font-semibold text-gray-900">
                                            {{ question.question_text }}
                                        </p>
                                    </div>
                                    <span
                                        class="ml-3 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700">
                                        {{ question.type }}
                                    </span>
                                </div>
                                <p class="mt-1 text-xs text-gray-500">
                                    {{ question.total_responses }} {{ question.total_responses === 1 ? 'response' :
                                        'responses' }}
                                </p>
                            </div>

                            <div v-if="question.has_chart && question.stats && question.stats.length"
                                class="mt-2 flex-1 flex flex-col">
                                <apexchart type="pie" height="320" :options="getPieChartOptions(question)"
                                    :series="getPieChartSeries(question)" />
                            </div>
                            <div v-else
                                class="mt-4 text-gray-500 text-sm bg-gray-50 border border-dashed border-gray-200 rounded-lg p-4">
                                <p class="flex items-center">
                                    <font-awesome-icon icon="info-circle" class="mr-2 text-gray-400" />
                                    This question is open-ended or does not have structured options, so a chart is not
                                    available.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </base-full-modal>

        <!-- Preview Modal -->
        <base-modal v-if="showPreviewModal" :title="previewModalTitle" @close="closePreviewModal" size="large">
            <div class="preview-container flex flex-col h-full">
                <!-- Loading State -->
                <div v-if="previewLoading" class="flex justify-center items-center h-full">
                    <base-spinner type="spin" class-list="h-12"></base-spinner>
                </div>

                <!-- Error State -->
                <div v-else-if="previewError" class="text-center py-8">
                    <font-awesome-icon icon="exclamation-triangle" class="text-red-500 text-4xl mb-4" />
                    <p class="text-red-600 mb-4">{{ previewError }}</p>
                    <base-button @click="downloadSurveyFile(previewFilePath, previewFileName)"
                        class="bg-blue-600 hover:bg-blue-700 text-white">
                        <font-awesome-icon icon="download" class="mr-2" />
                        Download Instead
                    </base-button>
                </div>

                <!-- Preview Content -->
                <div v-else class="preview-content flex-1 overflow-y-auto">
                    <!-- PDF Preview -->
                    <div v-if="isPdfFile(previewFileType)" class="pdf-preview">
                        <iframe :src="previewUrl" class="w-full h-96 border border-gray-300 rounded-lg"
                            frameborder="0"></iframe>
                    </div>

                    <!-- Image Preview -->
                    <div v-else-if="isImageFile(previewFileType)" class="image-preview text-center">
                        <img :src="previewUrl" :alt="previewFileName"
                            class="max-w-full max-h-96 mx-auto rounded-lg shadow-lg" />
                    </div>

                    <!-- Text File Preview -->
                    <div v-else-if="isTextFile(previewFileType)" class="text-preview">
                        <iframe :src="previewUrl" class="w-full h-96 border border-gray-300 rounded-lg"
                            frameborder="0"></iframe>
                    </div>

                    <!-- DOCX and Office File Preview -->
                    <div v-else-if="isDocxFile(previewFileType) || isOfficeDocument(previewFileType)"
                        class="office-preview">
                        <!-- Microsoft Office Online Viewer (First Priority) -->
                        <div v-if="previewUrl && !useMammothFallback">
                            <iframe :src="previewUrl" class="w-full h-96 border border-gray-300 rounded-lg"
                                frameborder="0"></iframe>
                        </div>

                        <!-- Mammoth HTML Conversion (Fallback for DOCX) -->
                        <div v-else-if="useMammothFallback && previewHtml && isDocxFile(previewFileType)" class="p-6">
                            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                <div class="flex items-start">
                                    <font-awesome-icon icon="info-circle"
                                        class="text-blue-500 text-lg mt-0.5 mr-3 flex-shrink-0" />
                                    <div class="text-sm text-blue-800">
                                        <p class="font-medium mb-1">Using HTML Preview</p>
                                        <p>Microsoft Office Online viewer is not available. Showing converted HTML
                                            preview. Some formatting
                                            may differ from the original document.</p>
                                    </div>
                                </div>
                            </div>
                            <div class="docx-wrapper mx-4">
                                <div v-html="previewHtml" class="docx-content prose max-w-none"></div>
                            </div>
                        </div>

                        <!-- Loading State -->
                        <div v-else class="text-center py-8">
                            <font-awesome-icon :icon="getFileIcon(previewFileType)"
                                :class="getFileIconColor(previewFileType)" class="text-4xl mb-4" />
                            <p class="text-gray-600">Loading document preview...</p>
                        </div>
                    </div>

                    <!-- Fallback for other file types -->
                    <div v-else class="fallback-preview text-center py-8">
                        <font-awesome-icon :icon="getFileIcon(previewFileType)"
                            :class="getFileIconColor(previewFileType)" class="text-6xl mb-4" />
                        <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ previewFileName }}</h3>
                        <p class="text-gray-600 mb-4">
                            Preview not available for this file type. Please download to view the content.
                        </p>
                        <div class="flex justify-center space-x-4">
                            <base-button @click="downloadSurveyFile(previewFilePath, previewFileName)"
                                class="bg-blue-600 hover:bg-blue-700 text-white">
                                <font-awesome-icon icon="download" class="mr-2" />
                                Download File
                            </base-button>
                            <base-button @click="closePreviewModal" class="bg-gray-600 hover:bg-gray-700 text-white">
                                Close
                            </base-button>
                        </div>
                    </div>
                </div>

                <!-- Modal Footer - Fixed at bottom -->
                <div v-if="!previewLoading && !previewError"
                    class="flex justify-end pt-4 pr-8 border-t border-gray-200 bg-white">
                    <base-button @click="downloadSurveyFile(previewFilePath, previewFileName)"
                        class="bg-blue-600 hover:bg-blue-700 text-white">
                        <font-awesome-icon icon="download" class="mr-2" />
                        Download
                    </base-button>
                </div>
            </div>
        </base-modal>
    </div>
</template>

<script>
import { gql } from '@apollo/client/core';
import mammoth from 'mammoth';
import BaseModal from '@/components/ui/BaseModal.vue';
import Pagination from '../../components/shared/pagination.vue';
import {
    getSectionTitleColor,
    getPageTitleColor,
    getSectionHeaderBorderStyle as buildSectionHeaderBorderStyle,
} from '@/utils/surveySectionColor';
import {
    getOtherCommentTextFromAnswer,
    formatSingleChoiceAnswerForDisplay,
    formatMultipleChoiceAnswerForDisplay,
} from '@/utils/surveyOtherOption';

const GET_SURVEY_RESPONSES = gql`
  query GetSurveyResponses($survey_id: ID!, $first: Int!, $page: Int!, $status_filter: String) {
    surveyResponses(survey_id: $survey_id, first: $first, page: $page, status_filter: $status_filter) {
      error
      error_code
      message
      data {
        id
        responded_by
        token
        status
        is_submitted
        answered_questions_count
        total_questions_count
        completion_percentage
        started_at
        last_activity_at
        completed_at
        engagement_duration_seconds
        created_at
        responder {
          id
          name
          email
        }
        contact_information {
          id
          name
          email
          country_code
          telephone
        }
        answers {
          id
          question_id
          answer_text
          option_id
          option_ids
          question {
            id
            question_text
            type
            order
            switch_left_label
            switch_right_label
            other_option_comment_enabled
            table_rows
            grid_row_titles
            file_upload_max_size_mb
            file_upload_types
            options {
              id
              option_text
              value
              order
            }
          }
          option {
            id
            option_text
          }
          options {
            id
            option_text
          }
        }
      }
      paginatorInfo {
        currentPage
        lastPage
        perPage
        total
        hasMorePages
        firstItem
        lastItem
      }
    }
  }
`;

const GET_SURVEY_BY_SLUG = gql`
  query GetSurveyBySlug($slug: String!) {
    surveyBySlug(slug: $slug) {
      error
      error_code
      message
      data {
        id
        title
        slug
        display_title
        description
        thumbnail_path
        thumbnail_url
        questions {
          id
          question_text
          type
          order
          section_title_color
          section_title_border_width
          section_title_border_color
          page_title_color
          page_title_border_width
          page_title_border_color
          add_other_option
          other_option_comment_enabled
          switch_left_label
          switch_right_label
          table_rows
          grid_row_titles
          options {
            id
            option_text
            value
            order
          }
        }
      }
    }
  }
`;

const GET_SURVEY_ANALYSIS = gql`
  query GetSurveyAnalysis($survey_id: ID!) {
    surveyAnalysis(survey_id: $survey_id) {
      error
      error_code
      message
      data {
        question_id
        question_text
        type
        has_chart
        total_responses
        stats {
          option_id
          label
          count
          percentage
        }
      }
    }
  }
`;

export default {
    name: 'SurveyResponses',
    components: {
        BaseModal,
        Pagination,
    },
    data() {
        return {
            survey: null,
            responses: [],
            loading: true,
            paginatorInfo: null,
            first: 10,
            page: 1,
            expandedResponses: {}, // Track which responses are expanded
            showAnalysisModal: false,
            analysisLoading: false,
            analysisQuestions: [],
            showPreviewModal: false,
            previewFilePath: null,
            previewFileName: null,
            previewFileType: null,
            previewUrl: null,
            previewHtml: null,
            previewLoading: false,
            previewError: null,
            useMammothFallback: false,
            responseViewMode: 'list',
            responseStatusTab: 'all',
            exportLoading: false,
            surveyLinkCopied: false,
        };
    },
    created() {
        this.loadSurvey();
    },
    computed: {
        emojiRatingOptions() {
            return {
                '1': { svg: require('@/assets/images/survey/emoji_rating/1.svg'), label: 'Very Sad' },
                '2': { svg: require('@/assets/images/survey/emoji_rating/2.svg'), label: 'Sad' },
                '3': { svg: require('@/assets/images/survey/emoji_rating/3.svg'), label: 'Neutral' },
                '4': { svg: require('@/assets/images/survey/emoji_rating/4.svg'), label: 'Happy' },
                '5': { svg: require('@/assets/images/survey/emoji_rating/5.svg'), label: 'Very Happy' },
            };
        },
        previewModalTitle() {
            return this.previewFileName ? `Preview: ${this.previewFileName}` : 'Preview File';
        },
        paginatorInfoWithItems() {
            if (!this.paginatorInfo) return null;
            const firstItem = this.paginatorInfo.firstItem || ((this.paginatorInfo.currentPage - 1) * this.paginatorInfo.perPage + 1);
            const lastItem = this.paginatorInfo.lastItem || Math.min(this.paginatorInfo.currentPage * this.paginatorInfo.perPage, this.paginatorInfo.total);
            return {
                ...this.paginatorInfo,
                firstItem,
                lastItem,
            };
        },
        sortedQuestions() {
            if (!this.survey || !this.survey.questions) return [];
            return [...this.survey.questions].sort((a, b) => (a.order || 0) - (b.order || 0));
        },
        emptyResponsesTitle() {
            if (this.responseStatusTab === 'completed') {
                return 'No fully completed responses';
            }
            if (this.responseStatusTab === 'partial') {
                return 'No partially answered responses';
            }
            return 'No responses yet';
        },
        emptyResponsesMessage() {
            if (this.responseStatusTab === 'completed') {
                return 'Fully completed responses will appear here once respondents answer every question.';
            }
            if (this.responseStatusTab === 'partial') {
                return 'Partially answered responses will appear here while respondents are still in progress.';
            }
            return 'Responses will appear here once users start or submit the survey';
        },
        surveyTakeLink() {
            if (!this.survey || !this.survey.slug) {
                return '';
            }
            return window.location.origin + '/#/survey/take/' + this.survey.slug;
        },
    },
    methods: {
        getSectionTitleColor(question) {
            return getSectionTitleColor(question);
        },
        getSectionHeaderBorderStyle(question) {
            return buildSectionHeaderBorderStyle(question);
        },
        getPageTitleColor(question) {
            return getPageTitleColor(question);
        },
        getPageBreakTitleText(question) {
            if (!question || !question.question_text) {
                return '';
            }
            return question.question_text.trim();
        },
        getResponseDisplayItems(response) {
            if (!this.sortedQuestions || this.sortedQuestions.length === 0 || !response) return [];
            let answerIndex = 0;
            return this.sortedQuestions.map((q) => {
                if (q.type === 'section_header') {
                    return { type: 'section', question: q };
                }
                if (q.type === 'page_break') {
                    return { type: 'page_break', question: q };
                }
                return {
                    type: 'answer',
                    question: q,
                    answer: (response.answers && Array.isArray(response.answers))
                        ? response.answers.find((a) => String(a.question_id) === String(q.id))
                        : null,
                    displayIndex: ++answerIndex,
                };
            });
        },
        getEmojiRatingSvg(value) {
            if (!value) return '';
            const strValue = String(value);
            const option = this.emojiRatingOptions[strValue];
            return option ? option.svg : '';
        },
        getEmojiRatingLabel(value) {
            if (!value) return '';
            const strValue = String(value);
            const option = this.emojiRatingOptions[strValue];
            return option ? option.label : '';
        },
        getRatingLabel(rating) {
            if (!rating || rating < 1 || rating > 5) return '';
            const labels = {
                1: 'Poor',
                2: 'Fair',
                3: 'Good',
                4: 'Very Good',
                5: 'Excellent'
            };
            return labels[rating] || '';
        },
        getSwitchAnswerLabel(question, answerText) {
            if (!question || question.type !== 'switch') return answerText != null ? answerText : '';
            const v = answerText != null && answerText !== '' ? String(answerText).trim() : null;
            if (v === '1') return question.switch_right_label || 'On';
            if (v === '0') return question.switch_left_label || 'Off';
            return answerText != null ? answerText : '';
        },
        getOtherCommentText(item) {
            if (!item || !item.answer || !item.question) {
                return '';
            }
            return getOtherCommentTextFromAnswer(item.answer, item.question);
        },
        sortedOptions(options) {
            if (!options) return [];
            return [...options].sort((a, b) => (a.order || 0) - (b.order || 0));
        },
        parseTableAnswer(answerText) {
            if (!answerText) return [];
            try {
                const data = typeof answerText === 'string' ? JSON.parse(answerText) : answerText;
                return Array.isArray(data) ? data : [];
            } catch {
                return [];
            }
        },
        parseGridAnswer(answerText) {
            if (!answerText) return {};
            try {
                const data = typeof answerText === 'string' ? JSON.parse(answerText) : answerText;
                return data && typeof data === 'object' && !Array.isArray(data) ? data : {};
            } catch {
                return {};
            }
        },
        isGridMultipleCellSelected(answerText, rowIndex, optionId) {
            const grid = this.parseGridAnswer(answerText);
            const row = grid[rowIndex];
            if (!Array.isArray(row) || row.length === 0) return false;
            const idStr = String(optionId);
            const idNum = parseInt(optionId, 10);
            return row.some(id => String(id) === idStr || parseInt(id, 10) === idNum);
        },
        getSurveyFileUrl(path) {
            if (!path) return '';
            const p = path.toString().trim();
            if (p.indexOf('http://') === 0 || p.indexOf('https://') === 0) return p;
            const base = process.env.VUE_APP_PUBLIC_URL || '';
            return `${base}/storage/${p}`;
        },
        getSurveyFileName(path) {
            if (!path) return 'Download file';
            const p = path.toString().trim();
            const parts = p.split('/');
            const last = parts[parts.length - 1] || '';
            // Backend stores uuid_filename; show filename part after first _
            const afterUuid = last.indexOf('_') !== -1 ? last.substring(last.indexOf('_') + 1) : last;
            return afterUuid || 'Download file';
        },
        async viewSurveyFile(path) {
            this.previewFilePath = path;
            this.previewFileName = this.getSurveyFileName(path);
            this.previewLoading = true;
            this.previewError = null;
            this.previewUrl = null;
            this.previewHtml = null;
            this.useMammothFallback = false;
            this.showPreviewModal = true;

            try {
                const response = await this.$apollo.mutate({
                    mutation: gql`
                        mutation DownloadSurveyFile($path: String!) {
                            downloadSurveyFile(path: $path) {
                                error
                                error_code
                                message
                                data {
                                    download_url
                                }
                            }
                        }
                    `,
                    variables: {
                        path: path
                    }
                });

                if (!response.data.downloadSurveyFile.error) {
                    const downloadUrl = response.data.downloadSurveyFile.data.download_url;

                    // Get file type
                    const contentResponse = await this.$apollo.mutate({
                        mutation: gql`
                            mutation GetSurveyFileContent($path: String!) {
                                getSurveyFileContent(path: $path) {
                                    error
                                    error_code
                                    message
                                    data {
                                        file_type
                                    }
                                }
                            }
                        `,
                        variables: {
                            path: path
                        }
                    });

                    if (!contentResponse.data.getSurveyFileContent.error) {
                        this.previewFileType = contentResponse.data.getSurveyFileContent.data.file_type;
                    }

                    // Handle DOCX and other Office files
                    if (this.isDocxFile(this.previewFileType) || this.isOfficeDocument(this.previewFileType)) {
                        // First priority: Try Microsoft Office Online viewer
                        try {
                            const encodedUrl = encodeURIComponent(downloadUrl);
                            const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
                            this.previewUrl = officeViewerUrl;
                        } catch (officeError) {
                            console.error('Error setting up Office Online viewer:', officeError);
                            // Fall back to mammoth for DOCX files only if Office Online setup fails
                            if (this.isDocxFile(this.previewFileType)) {
                                try {
                                    const conversionResult = await this.convertDocxToHtml(path);
                                    this.previewHtml = conversionResult.html;
                                    this.useMammothFallback = true;
                                    this.previewUrl = null;
                                } catch (conversionError) {
                                    console.error('Error converting DOCX:', conversionError);
                                    this.previewError = 'Failed to load document preview. Please download the file instead.';
                                }
                            } else {
                                this.previewUrl = downloadUrl;
                            }
                        }
                    } else {
                        // For other file types, use the URL directly
                        this.previewUrl = downloadUrl;
                    }
                } else {
                    this.previewError = response.data.downloadSurveyFile.message;
                }
            } catch (error) {
                console.error('Error loading file for preview:', error);
                this.previewError = 'Failed to load the file for preview. Please try again.';
            } finally {
                this.previewLoading = false;
            }
        },
        closePreviewModal() {
            this.showPreviewModal = false;
            this.previewFilePath = null;
            this.previewFileName = null;
            this.previewFileType = null;
            this.previewUrl = null;
            this.previewHtml = null;
            this.previewError = null;
            this.useMammothFallback = false;
        },
        async convertDocxToHtml(filePath) {
            try {
                const response = await this.$apollo.mutate({
                    mutation: gql`
                        mutation GetSurveyFileContent($path: String!) {
                            getSurveyFileContent(path: $path) {
                                error
                                error_code
                                message
                                data {
                                    content
                                    file_type
                                }
                            }
                        }
                    `,
                    variables: {
                        path: filePath
                    }
                });

                if (response.data.getSurveyFileContent.error) {
                    throw new Error(response.data.getSurveyFileContent.message);
                }

                // Convert base64 content to ArrayBuffer
                const base64Content = response.data.getSurveyFileContent.data.content;
                const binaryString = atob(base64Content);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                // Convert DOCX to HTML using mammoth
                const result = await mammoth.convertToHtml({ arrayBuffer: bytes.buffer });

                return {
                    html: result.value,
                    messages: result.messages
                };
            } catch (error) {
                console.error('Error converting DOCX to HTML:', error);
                throw error;
            }
        },
        async downloadSurveyFile(path, filename) {
            try {
                const response = await this.$apollo.mutate({
                    mutation: gql`
                        mutation DownloadSurveyFile($path: String!) {
                            downloadSurveyFile(path: $path) {
                                error
                                error_code
                                message
                                data {
                                    download_url
                                }
                            }
                        }
                    `,
                    variables: {
                        path: path
                    }
                });

                if (response.data.downloadSurveyFile.error || !response.data.downloadSurveyFile.data.download_url) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Download Failed',
                        text: response.data.downloadSurveyFile.message || 'File URL not available.',
                    });
                    return;
                }

                const downloadUrl = response.data.downloadSurveyFile.data.download_url;

                // Convert base64 content to blob for download
                try {
                    const contentResponse = await this.$apollo.mutate({
                        mutation: gql`
                            mutation GetSurveyFileContent($path: String!) {
                                getSurveyFileContent(path: $path) {
                                    error
                                    error_code
                                    message
                                    data {
                                        content
                                        file_type
                                    }
                                }
                            }
                        `,
                        variables: {
                            path: path
                        }
                    });

                    if (!contentResponse.data.getSurveyFileContent.error) {
                        const base64Content = contentResponse.data.getSurveyFileContent.data.content;
                        const fileType = contentResponse.data.getSurveyFileContent.data.file_type;
                        const binaryString = atob(base64Content);
                        const bytes = new Uint8Array(binaryString.length);
                        for (let i = 0; i < binaryString.length; i++) {
                            bytes[i] = binaryString.charCodeAt(i);
                        }
                        const blob = new Blob([bytes], { type: fileType });
                        const blobUrl = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = blobUrl;
                        link.download = filename || 'download';
                        link.style.display = 'none';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(blobUrl);
                    } else {
                        // Fallback to direct URL download
                        const link = document.createElement('a');
                        link.href = downloadUrl;
                        link.download = filename || 'download';
                        link.target = '_blank';
                        link.rel = 'noopener noreferrer';
                        link.style.display = 'none';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }
                } catch (error) {
                    // Fallback to direct URL download
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.download = filename || 'download';
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    link.style.display = 'none';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            } catch (error) {
                console.error('Error downloading file:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: 'Download Failed',
                    text: 'Failed to download the file. Please try again.',
                });
            }
        },
        getFileIcon(fileType) {
            const iconMap = {
                'application/pdf': 'file-pdf',
                'application/msword': 'file-word',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'file-word',
                'application/vnd.ms-excel': 'file-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'file-excel',
                'application/vnd.ms-powerpoint': 'file-powerpoint',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'file-powerpoint',
                'text/plain': 'file-alt',
                'image/jpeg': 'file-image',
                'image/jpg': 'file-image',
                'image/png': 'file-image',
                'image/gif': 'file-image',
                'image/webp': 'file-image',
            };
            return iconMap[fileType] || 'file';
        },
        getFileIconColor(fileType) {
            const colorMap = {
                'application/pdf': 'text-red-600',
                'application/msword': 'text-blue-600',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'text-blue-600',
                'application/vnd.ms-excel': 'text-green-600',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'text-green-600',
                'application/vnd.ms-powerpoint': 'text-orange-600',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'text-orange-600',
                'text/plain': 'text-gray-600',
                'image/jpeg': 'text-purple-600',
                'image/jpg': 'text-purple-600',
                'image/png': 'text-purple-600',
                'image/gif': 'text-purple-600',
                'image/webp': 'text-purple-600',
            };
            return colorMap[fileType] || 'text-gray-600';
        },
        isImageFile(fileType) {
            const imageTypes = [
                'image/jpeg',
                'image/jpg',
                'image/png',
                'image/gif',
                'image/bmp',
                'image/webp',
                'image/svg+xml',
                'image/tiff',
                'image/tif'
            ];
            return imageTypes.includes(fileType);
        },
        isPdfFile(fileType) {
            const pdfTypes = ['application/pdf'];
            return pdfTypes.includes(fileType);
        },
        isTextFile(fileType) {
            const textTypes = [
                'text/plain',
                'text/html',
                'text/css',
                'text/javascript',
                'text/csv',
                'application/json',
                'application/xml',
                'text/xml'
            ];
            return textTypes.includes(fileType);
        },
        isOfficeDocument(fileType) {
            const officeTypes = [
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-powerpoint',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation'
            ];
            return officeTypes.includes(fileType);
        },
        isDocxFile(fileType) {
            const docxTypes = [
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            ];
            return docxTypes.includes(fileType);
        },
        downloadSignature(dataURL, questionText) {
            try {
                if (!dataURL) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Download Failed',
                        text: 'Signature data not available.',
                    });
                    return;
                }

                // Convert base64 data URL to blob
                const byteString = atob(dataURL.split(',')[1]);
                const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
                const ab = new ArrayBuffer(byteString.length);
                const ia = new Uint8Array(ab);
                for (let i = 0; i < byteString.length; i++) {
                    ia[i] = byteString.charCodeAt(i);
                }
                const blob = new Blob([ab], { type: mimeString });

                // Create download link
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;

                // Generate filename from question text
                const sanitizedQuestionText = questionText
                    ? questionText.replace(/[^a-z0-9]/gi, '_').toLowerCase().substring(0, 30)
                    : 'signature';
                link.download = `${sanitizedQuestionText}_signature.png`;

                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // Clean up the object URL
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Error downloading signature:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: 'Download Failed',
                    text: 'Failed to download the signature. Please try again.',
                });
            }
        },
        getChartColors() {
            return [
                '#1f77b4',
                '#ff7f0e',
                '#2ca02c',
                '#d62728',
                '#9467bd',
                '#8c564b',
                '#e377c2',
                '#7f7f7f',
                '#bcbd22',
                '#17becf',
            ];
        },
        getPieChartSeries(question) {
            if (!question || !question.stats || !question.stats.length) {
                return [];
            }
            return question.stats.map(function (item) {
                return item.count;
            });
        },
        getPieChartOptions(question) {
            const labels = (question && question.stats)
                ? question.stats.map(function (item) {
                    return item.label;
                })
                : [];
            const colors = this.getChartColors();

            return {
                labels: labels,
                colors: colors,
                legend: {
                    position: 'bottom',
                    formatter: function (seriesName, opts) {
                        const seriesIndex = opts.seriesIndex;
                        const counts = opts.w.config.series;
                        const value = counts && counts.length > seriesIndex ? counts[seriesIndex] : 0;
                        return seriesName + ' - ' + value + ' responses';
                    },
                },
                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        return val.toFixed(1) + '%';
                    },
                },
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return val + ' responses';
                        },
                    },
                },
                chart: {
                    type: 'pie',
                },
            };
        },
        openAnalysisModal() {
            if (!this.survey || !this.survey.id) {
                return;
            }
            this.showAnalysisModal = true;
            if (!this.analysisQuestions || this.analysisQuestions.length === 0) {
                this.loadAnalysis();
            }
        },
        closeAnalysisModal() {
            this.showAnalysisModal = false;
        },
        async loadAnalysis() {
            if (!this.survey || !this.survey.id) {
                return;
            }
            this.analysisLoading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: GET_SURVEY_ANALYSIS,
                    variables: {
                        survey_id: this.survey.id,
                    },
                    fetchPolicy: 'network-only',
                });

                if (!data || !data.surveyAnalysis) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Failed to load survey analysis.',
                    });
                    return;
                }

                const result = data.surveyAnalysis;

                if (result.error) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: result.message || 'Failed to load survey analysis.',
                    });
                    return;
                }

                this.analysisQuestions = result.data || [];
            } catch (error) {
                let errorMessage = 'Failed to load survey analysis.';
                if (error.graphQLErrors && error.graphQLErrors.length > 0 && error.graphQLErrors[0].message) {
                    errorMessage = error.graphQLErrors[0].message;
                } else if (error.networkError && error.networkError.message) {
                    errorMessage = error.networkError.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }
                this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: errorMessage,
                });
            } finally {
                this.analysisLoading = false;
            }
        },
        async loadSurvey() {
            try {
                const slug = this.$route.params.slug;
                const { data } = await this.$apollo.query({
                    query: GET_SURVEY_BY_SLUG,
                    variables: { slug: slug },
                    fetchPolicy: 'network-only',
                });

                if (!data || !data.surveyBySlug) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Failed to load survey.',
                    });
                    this.goBack();
                    return;
                }

                const result = data.surveyBySlug;

                if (result.error) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: result.message || 'Failed to load survey.',
                    });
                    this.goBack();
                    return;
                }

                if (!result.data) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Survey Not Found',
                        text: 'The survey you are looking for does not exist or is inactive.',
                    });
                    this.goBack();
                    return;
                }

                this.survey = result.data;
                // Load responses after we have the survey ID
                this.loadResponses();
            } catch (error) {
                console.error(error);
                let errorMessage = 'Failed to load survey.';
                if (error.graphQLErrors && error.graphQLErrors.length > 0 && error.graphQLErrors[0].message) {
                    errorMessage = error.graphQLErrors[0].message;
                } else if (error.networkError && error.networkError.message) {
                    errorMessage = error.networkError.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }
                this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: errorMessage,
                });
                this.goBack();
            }
        },
        async loadResponses() {
            if (!this.survey || !this.survey.id) {
                return;
            }

            this.loading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: GET_SURVEY_RESPONSES,
                    variables: {
                        survey_id: this.survey.id,
                        first: this.first,
                        page: this.page,
                        status_filter: this.responseStatusTab === 'all' ? null : this.responseStatusTab,
                    },
                    fetchPolicy: 'network-only',
                });

                if (data.surveyResponses.error) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Oops...',
                        text: data.surveyResponses.message,
                    });
                } else {
                    this.responses = data.surveyResponses.data.map(response => {
                        // Create a new response object with sorted answers
                        const sortedResponse = { ...response };
                        if (response.answers && Array.isArray(response.answers)) {
                            sortedResponse.answers = [...response.answers].sort((a, b) => {
                                const orderA = (a.question && a.question.order !== undefined && a.question.order !== null) ? a.question.order : 999;
                                const orderB = (b.question && b.question.order !== undefined && b.question.order !== null) ? b.question.order : 999;
                                return orderA - orderB;
                            });
                        }
                        return sortedResponse;
                    });
                    this.paginatorInfo = data.surveyResponses.paginatorInfo;
                    // Initialize all responses as collapsed by default
                    this.responses.forEach(response => {
                        this.expandedResponses[response.id] = false;
                    });
                }
            } catch (error) {
                this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: 'Failed to load responses',
                });
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        loadPage(page) {
            this.page = page;
            this.loadResponses();
        },
        setPage(page) {
            this.loadPage(page);
        },
        setResponseStatusTab(tab) {
            if (this.responseStatusTab === tab) {
                return;
            }
            this.responseStatusTab = tab;
            this.page = 1;
            this.expandedResponses = {};
            this.loadResponses();
        },
        goBack() {
            this.$router.push('/survey');
        },
        async copySurveyLink() {
            const link = this.surveyTakeLink;
            if (!link) {
                return;
            }
            try {
                await navigator.clipboard.writeText(link);
                this.surveyLinkCopied = true;
                setTimeout(() => {
                    this.surveyLinkCopied = false;
                }, 2000);
            } catch (err) {
                const textArea = document.createElement('textarea');
                textArea.value = link;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                try {
                    document.execCommand('copy');
                    this.surveyLinkCopied = true;
                    setTimeout(() => {
                        this.surveyLinkCopied = false;
                    }, 2000);
                } catch (e) {
                    console.error('Copy failed:', e);
                }
                document.body.removeChild(textArea);
            }
        },
        formatDate(date) {
            if (!date) return '';
            return new Date(date).toLocaleString();
        },
        toggleResponse(responseId) {
            this.expandedResponses[responseId] = !this.expandedResponses[responseId];
        },
        getRespondentName(response) {
            if (response.responder && response.responder.name) {
                return response.responder.name;
            }
            if (response.contact_information && response.contact_information.name) {
                return response.contact_information.name;
            }
            return 'Anonymous';
        },
        getCompletionPercentage(response) {
            if (!response) {
                return 0;
            }
            if (response.completion_percentage !== undefined && response.completion_percentage !== null) {
                return Math.min(100, Math.max(0, Number(response.completion_percentage)));
            }
            const total = response.total_questions_count || 0;
            const answered = response.answered_questions_count || 0;
            if (!total) {
                return 0;
            }
            return Math.min(100, Math.round((answered / total) * 100));
        },
        getProgressBarClass(response) {
            const percentage = this.getCompletionPercentage(response);
            if (percentage >= 100) {
                return 'survey-response-progress-fill-complete';
            }
            return 'survey-response-progress-fill-partial';
        },
        getResponseStatusLabel(response) {
            if (response && response.status === 'completed') {
                return 'Fully Completed';
            }
            return 'Partially Answered';
        },
        getSubmissionLabel(response) {
            if (response && response.is_submitted) {
                return 'Submitted';
            }
            return 'Not Submitted';
        },
        formatCountValue(value) {
            if (value === undefined || value === null || value === '') {
                return 0;
            }
            return parseInt(value, 10);
        },
        formatEngagementDuration(response) {
            if (!response) {
                return '—';
            }
            let seconds = response.engagement_duration_seconds;
            if (seconds === undefined || seconds === null) {
                seconds = 0;
            }
            seconds = parseInt(seconds, 10);
            if (!seconds || seconds < 1) {
                return '< 1 min';
            }
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const remainingSeconds = seconds % 60;
            if (hours > 0) {
                return `${hours}h ${minutes}m`;
            }
            if (minutes > 0) {
                return `${minutes}m ${remainingSeconds}s`;
            }
            return `${remainingSeconds}s`;
        },
        getTableCellAnswer(response, question) {
            if (!response.answers || !Array.isArray(response.answers)) return '';
            const answer = response.answers.find(function (a) { return a.question_id === question.id; });
            if (!answer) return '';
            return this.formatAnswerForTable(answer, question);
        },
        formatAnswerForTable(answer, question) {
            const type = question.type || 'text';
            if (type === 'radio' || type === 'select') {
                return formatSingleChoiceAnswerForDisplay(answer, question);
            }
            if (type === 'checkbox') {
                return formatMultipleChoiceAnswerForDisplay(answer, question);
            }
            if (answer.answer_text !== null && answer.answer_text !== '') {
                if (type === 'emoji_rating') {
                    const labels = { '1': 'Very Sad', '2': 'Sad', '3': 'Neutral', '4': 'Happy', '5': 'Very Happy' };
                    return labels[answer.answer_text] || answer.answer_text + '/5';
                }
                if (type === 'rating') return answer.answer_text + '/5';
                if (type === 'switch') {
                    const v = String(answer.answer_text).trim();
                    if (v === '1') return question.switch_right_label || 'On';
                    if (v === '0') return question.switch_left_label || 'Off';
                }
                if (type === 'table') return this.formatTableAnswerForCell(answer.answer_text);
                if (type === 'grid_single_select' || type === 'grid_multiple_select') return this.formatGridAnswerForCell(answer.answer_text, question);
                if (type === 'file_upload') return this.getSurveyFileName(answer.answer_text);
                if (type === 'signature') return '[Signature]';
                return answer.answer_text;
            }
            if (answer.option && answer.option.option_text) return answer.option.option_text;
            if (answer.options && answer.options.length > 0) {
                return answer.options.map(function (o) { return o.option_text; }).join(', ');
            }
            return '';
        },
        formatTableAnswerForCell(answerText) {
            if (!answerText) return '';
            try {
                const data = typeof answerText === 'string' ? JSON.parse(answerText) : answerText;
                if (!Array.isArray(data)) return answerText;
                return data.map(function (row) {
                    return Array.isArray(row) ? row.join(' | ') : String(row);
                }).join('; ');
            } catch (e) {
                return answerText;
            }
        },
        formatGridAnswerForCell(answerText, question) {
            if (!answerText) return '';
            try {
                const data = typeof answerText === 'string' ? JSON.parse(answerText) : answerText;
                if (!data || typeof data !== 'object' || Array.isArray(data)) return answerText;
                const options = (question.options || []).reduce(function (acc, o) { acc[o.id] = o.option_text; return acc; }, {});
                const rowTitles = question.grid_row_titles || [];
                const parts = [];
                for (let i = 0; i < rowTitles.length; i++) {
                    const val = data[i];
                    const rowTitle = rowTitles[i] || 'Row ' + (i + 1);
                    if (Array.isArray(val)) {
                        parts.push(rowTitle + ': ' + val.map(function (id) { return options[id] || id; }).join(', '));
                    } else if (val != null) {
                        parts.push(rowTitle + ': ' + (options[val] || val));
                    }
                }
                return parts.join('; ');
            } catch (e) {
                return answerText;
            }
        },
        async exportToExcel() {
            if (!this.survey || !this.survey.id) return;
            this.exportLoading = true;
            try {
                const { data } = await this.$apollo.mutate({
                    mutation: gql`
                        mutation ExportSurveyResponses($survey_id: ID!, $status_filter: String) {
                            exportSurveyResponses(survey_id: $survey_id, status_filter: $status_filter) {
                                error
                                error_code
                                message
                                data {
                                    download_url
                                }
                            }
                        }
                    `,
                    variables: {
                        survey_id: this.survey.id,
                        status_filter: this.responseStatusTab === 'all' ? null : this.responseStatusTab,
                    },
                });
                const result = data && data.exportSurveyResponses ? data.exportSurveyResponses : null;
                if (result && result.error) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Export Failed',
                        text: result.message || 'Failed to export survey responses.',
                    });
                    return;
                }
                if (result && result.data && result.data.download_url) {
                    const url = result.data.download_url;
                    const base = process.env.VUE_APP_PUBLIC_URL || '';
                    const fullUrl = url.indexOf('http') === 0 ? url : base + url;
                    const link = document.createElement('a');
                    link.href = fullUrl;
                    link.download = 'survey_responses_' + (this.survey.title || 'export') + '.xlsx';
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    link.style.display = 'none';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    this.$swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'success',
                        title: 'Excel export downloaded',
                        showConfirmButton: false,
                        timer: 2000,
                        timerProgressBar: true,
                    });
                }
            } catch (error) {
                console.error('Export error:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: 'Export Failed',
                    text: error && error.message ? error.message : 'Failed to export survey responses.',
                });
            } finally {
                this.exportLoading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.slide-fade-enter-active {
    transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
    transition: all 0.3s ease-in;
}

.slide-fade-enter-from {
    transform: translateY(-10px);
    opacity: 0;
}

.slide-fade-leave-to {
    transform: translateY(-10px);
    opacity: 0;
}

.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.sr-respondent-cell {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
    max-width: 200px;
}

.sr-respondent-col {
    min-width: 300px;
}

.sr-respondent-cell-table {
    max-width: none;
    gap: 6px;
}

.sr-respondent-cell-list {
    flex-direction: row;
    align-items: center;
    flex-wrap: nowrap;
    max-width: none;
    gap: 8px;
}

.sr-respondent-cell-list .sr-respondent-name {
    flex-shrink: 1;
    min-width: 0;
}

.sr-respondent-name {
    display: block;
    font-weight: 600;
    color: #111827;
    line-height: 1.35;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.sr-respondent-token {
    display: block;
    font-size: 11px;
    line-height: 1.3;
    color: #6b7280;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    word-break: break-all;
}

.sr-respondent-token-tile {
    display: inline-flex;
    align-items: center;
    width: fit-content;
    max-width: 100%;
    padding: 3px 6px;
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    white-space: nowrap;
}

.sr-respondent-token-label {
    flex-shrink: 0;
    margin-right: 4px;
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: #64748b;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.sr-respondent-token-value {
    flex-shrink: 0;
    font-size: 9px;
    line-height: 1.15;
    color: #334155;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    white-space: nowrap;
}

.survey-response-progress-wrap {
    min-width: 120px;
}

.survey-response-progress-track {
    width: 100%;
    height: 8px;
    background-color: #e5e7eb;
    border-radius: 9999px;
    overflow: hidden;
}

.survey-response-progress-fill {
    height: 100%;
    border-radius: 9999px;
    transition: width 0.3s ease;
}

.survey-response-progress-fill-partial {
    background: linear-gradient(90deg, #f59e0b, #fbbf24);
}

.survey-response-progress-fill-complete {
    background: linear-gradient(90deg, #16a34a, #22c55e);
}

.survey-response-progress-label {
    display: block;
    margin-top: 4px;
    font-size: 11px;
    color: #4b5563;
    font-weight: 600;
}

.survey-response-partial-badge {
    margin-left: 6px;
    color: #b45309;
}

.survey-response-complete-badge {
    margin-left: 6px;
    color: #15803d;
}

.sr-survey-link-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background-color: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.sr-survey-link-label {
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6b7280;
}

.sr-survey-link-field {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    background-color: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    overflow: hidden;
}

.sr-survey-link-icon {
    flex-shrink: 0;
    font-size: 12px;
    color: #2563eb;
}

.sr-survey-link-text {
    font-size: 12px;
    color: #374151;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.sr-survey-link-copy {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    font-size: 12px;
    font-weight: 600;
    color: #ffffff;
    background-color: #2563eb;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.15s ease;
}

.sr-survey-link-copy:hover {
    background-color: #1d4ed8;
}

.sr-survey-link-copy-icon {
    font-size: 11px;
}
</style>
